import React, { useState } from 'react';
import { REPORT_SENT, reportHeading, type ReportTargetKind } from '../domain/safety';
import { useRepositories } from '../hooks/useRepositories';
import { ActionError } from './PageStates';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export type ReportTarget = { kind: ReportTargetKind; id: string };

/**
 * Reporting a comment, listing, person or collection to the app's admins
 * (017 FR-026–FR-029): an optional note in my own words, no categories, then
 * "Report sent." Nobody else is told.
 */
export default function ReportDialog({
  target,
  open,
  onOpenChange,
}: {
  target: ReportTarget;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl border-none bg-background">
        {open && <ReportBody target={target} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function ReportBody({ target, onClose }: { target: ReportTarget; onClose: () => void }) {
  const { safety } = useRepositories();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await safety.report({ targetKind: target.kind, targetId: target.id, note: note.trim() });
      setSent(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <>
        <DialogHeader>
          <DialogTitle className="text-xl">{reportHeading(target.kind)}</DialogTitle>
          <DialogDescription role="status">{REPORT_SENT}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button className="rounded-full" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-xl">{reportHeading(target.kind)}</DialogTitle>
        <DialogDescription>
          The app's admins will look at it. Nobody else is told, including whoever you're reporting.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label htmlFor="report-note">Add a note (optional)</Label>
        <Textarea
          id="report-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Tell us what's wrong, in your own words."
          className="min-h-24 rounded-2xl"
        />
      </div>
      {error !== null && <ActionError error={error} />}
      <DialogFooter className="gap-2">
        <Button variant="ghost" className="rounded-full" onClick={onClose}>
          Cancel
        </Button>
        <Button className="rounded-full" disabled={busy} onClick={send}>
          Send
        </Button>
      </DialogFooter>
    </>
  );
}
